import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21 } from "styled-components";
import React from "react";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Grid } from "~/features/ui/grid";
import { IconButton } from "~/features/ui/icon-button";
import { Media } from "~/features/ui/media";
import { hasItems, takeFirst } from "~/features/app/utils";
import { VideoControls } from "~/features/ui/video/video-controls";
import { type Mode, type Theme } from "~/features/ui/get-theme-style";
import Minus from "~/public/icons/minus.svg";
import Plus from "~/public/icons/plus.svg";
import { RichText } from "~/features/ui/rich-text";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { AccordionListItemBulletPoint } from "~/features/blocks/inventory/accordion-list/accordion-list-item-bullet-point";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
const fragment = graphql(`
  fragment AccordionListItem on blocks_accordionListItem_BlockType {
    id
    title: baseText
    shortDescription: baseText2
    text: baseRichText
    link: baseLink {
      ...Link
    }
    media: baseMedia {
      ...Media
    }
    bulletPoints: children {
      ...AccordionListItemBulletPoint
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  mode: Mode;
  theme?: Theme;
  isOpen: boolean;
  count?: string;
};
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type AccordionListItemViewProps = {
  bulletPoints?: React.ReactNode;
  count?: string;
  hasMedia?: boolean;
  isOpen: boolean;
  link?: PlainLink | null;
  mediaDesktop?: React.ReactNode;
  mediaMobile?: React.ReactNode;
  mode: Mode;
  shortDescription?: string | null;
  text?: React.ReactNode;
  theme?: string | null;
  title?: string | null;
  value: string;
};
export function AccordionListItemView({
  bulletPoints,
  count,
  hasMedia = false,
  isOpen,
  link,
  mediaDesktop,
  mediaMobile,
  mode,
  shortDescription,
  text,
  theme,
  title,
  value
}: AccordionListItemViewProps) {
  const icon = getPlainLinkIcon(link);

  // Match the section: true white for the "white" theme, grey-20 for "grey".
  // In dark mode the "white" theme must use the dark surface, otherwise the
  // white background collides with the white `text-primary` (data-theme=dark).
  // bg-primary is mode-aware (white in light, near-black in dark) so dark-mode
  // accordions stay legible; grey theme keeps grey-20.
  const surface = theme === "grey" ? {
    "backgroundColor": "var(--bg-secondary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  };
  const hoverId = `id-${value}`;
  return <RadixAccordion.Item value={value} data-theme={mode as Mode}>
      <_StyledRadixAccordionHeader $_css={[{
      "position": "relative",
      "zIndex": "10",
      "color": "var(--text-primary)"
    }, surface]}>
        <_StyledRadixAccordionTrigger aria-label={title ?? undefined} id={hoverId} $_css2={[{
        "display": "flex",
        "width": "100%",
        "alignItems": "center",
        "justifyContent": "space-between",
        "gap": "0.8rem",
        "paddingLeft": "var(--spacing-md)",
        "paddingRight": "var(--spacing-md)",
        "paddingTop": "var(--spacing-lg)",
        "paddingBottom": "var(--spacing-lg)",
        "textAlign": "left"
      }, count && {
        "paddingLeft": "var(--spacing-lg)",
        "paddingRight": "var(--spacing-lg)",
        "@media (min-width: 1024px)": {
          "paddingLeft": "var(--spacing-xl)",
          "paddingRight": "var(--spacing-xl)",
          "paddingTop": "var(--spacing-lg)",
          "paddingBottom": "var(--spacing-lg)"
        }
      }]}>
          <_StyledDiv>
            <_StyledDiv2>
              {count && <_StyledSpan>
                  {count}
                </_StyledSpan>}
              {title && <_StyledH>{title}</_StyledH>}
            </_StyledDiv2>
            {shortDescription && <_StyledP>{shortDescription}</_StyledP>}
          </_StyledDiv>
          <IconButton as="div" icon={{
          component: isOpen ? Minus : Plus
        }} hoverId={hoverId} />
        </_StyledRadixAccordionTrigger>
      </_StyledRadixAccordionHeader>
      <_StyledRadixAccordionContent $_css3={[{
      "overflow": "hidden",
      "@keyframes radix-accordion-collapse-keyframes": {
        "from": {
          "height": "var(--radix-accordion-content-height)"
        },
        "to": {
          "height": "0"
        }
      },
      "&[data-state=\"closed\"]": {
        "animation": "radix-accordion-collapse-keyframes 250ms ease-in-out"
      },
      "@keyframes radix-accordion-expand-keyframes": {
        "from": {
          "height": "0"
        },
        "to": {
          "height": "var(--radix-accordion-content-height)"
        }
      },
      "&[data-state=\"open\"]": {
        "animation": "radix-accordion-expand-keyframes 250ms ease-in-out"
      }
    }]}>
        {(hasMedia || text) && <>
            {hasMedia ? <_StyledGrid $_css4={[{
          "position": "relative",
          "borderTopWidth": "1px",
          "borderColor": "var(--stroke-primary)",
          "@media (min-width: 1024px)": {
            "minHeight": "560px"
          }
        }, surface, isOpen && {
          "opacity": "1"
        }]}>
                <_StyledDiv3 $_css5={[{
            "position": "relative",
            "zIndex": "10",
            "gridColumn": "1 / -1",
            "display": "flex",
            "height": "100%",
            "flexDirection": "column",
            "justifyContent": "flex-end",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 5 / span 5",
              "paddingTop": "16rem"
            }
          }]}>
                  <_StyledDiv4 $_css6={[{
              "gridColumn": "1 / -1",
              "display": "block",
              "borderBottomWidth": "1px",
              "borderColor": "var(--stroke-primary)",
              "padding": "var(--spacing-md)",
              "@media (min-width: 1024px)": {
                "display": "none"
              }
            }]}>{mediaMobile}</_StyledDiv4>
                  {(text || isLinkValid(link)) && <_StyledDiv5 $_css7={[{
              "paddingLeft": "var(--spacing-lg)",
              "paddingRight": "var(--spacing-lg)",
              "paddingTop": "var(--spacing-md)",
              "paddingBottom": "var(--spacing-md)",
              "@media (min-width: 1024px)": {
                "padding": "var(--spacing-3xl)"
              }
            }]}>
                      {text && <_StyledDiv6 $_css8={[{
                "color": "var(--text-primary)",
                "opacity": "0",
                "transitionProperty": "opacity",
                "transitionDelay": "200ms",
                "transitionDuration": "250ms",
                "transitionTimingFunction": "ease-in-out"
              }, isOpen && {
                "opacity": "1"
              }]}>
                          {text}
                        </_StyledDiv6>}
                      {isLinkValid(link) && <_StyledDiv7 $_css9={[{
                "marginTop": "var(--spacing-lg)",
                "opacity": "0",
                "transitionProperty": "opacity",
                "transitionDelay": "250ms",
                "transitionDuration": "250ms",
                "transitionTimingFunction": "ease-in-out",
                "@media (min-width: 1024px)": {
                  "marginTop": "var(--spacing-xl)"
                }
              }, isOpen && {
                "opacity": "1"
              }]}>
                          <Button as={Link} {...link} variant="underline" icon={icon} />
                        </_StyledDiv7>}
                    </_StyledDiv5>}
                </_StyledDiv3>
                <_StyledDiv8>
                  <_StyledDiv9>{mediaDesktop}</_StyledDiv9>
                </_StyledDiv8>
              </_StyledGrid> : text && <_StyledDiv10 $_css10={[{
          "borderTopWidth": "1px",
          "borderColor": "var(--stroke-primary)",
          "paddingLeft": "var(--spacing-lg)",
          "paddingRight": "var(--spacing-lg)",
          "paddingTop": "var(--spacing-md)",
          "paddingBottom": "var(--spacing-md)",
          "@media (min-width: 1024px)": {
            "padding": "var(--spacing-xl)"
          }
        }]}>
                  <_StyledDiv11 $_css11={[{
            "opacity": "0",
            "transitionProperty": "opacity",
            "transitionDelay": "200ms",
            "transitionDuration": "250ms",
            "transitionTimingFunction": "ease-in-out"
          }, isOpen && {
            "opacity": "1"
          }]}>
                    {text}
                  </_StyledDiv11>
                </_StyledDiv10>}
          </>}
        {bulletPoints}
      </_StyledRadixAccordionContent>
    </RadixAccordion.Item>;
}
export function AccordionListItem(props: Props) {
  const {
    count,
    isOpen,
    mode,
    theme,
    ...rest
  } = props;
  const {
    id,
    title,
    shortDescription,
    text,
    media: _media,
    bulletPoints = [],
    link: _link
  } = useFragment(fragment, rest);
  const mobileVideoRef = React.useRef<HTMLVideoElement>(null);
  const desktopVideoRef = React.useRef<HTMLVideoElement>(null);
  const media = takeFirst(_media);
  const link = takeFirst(_link);
  return <AccordionListItemView bulletPoints={hasItems(bulletPoints) ? <_StyledDiv12 $_css12={[{
    "position": "relative",
    "zIndex": "10",
    "display": "flex",
    "flexDirection": "column",
    "rowGap": "var(--spacing-lg)",
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)",
    "padding": "var(--spacing-lg)",
    "@media (min-width: 1024px)": {
      "padding": "var(--spacing-xl)"
    }
  }]}>
            {bulletPoints.map((bulletPoint, idx) => <AccordionListItemBulletPoint key={idx} {...bulletPoint} />)}
          </_StyledDiv12> : undefined} count={count} hasMedia={Boolean(media)} isOpen={isOpen} link={link as PlainLink | null} mediaDesktop={media ? <Media {...media} showCaption imageProps={{
    fit: "cover",
    sizes: "lg:50vw"
  }} inlineVideoProps={{
    fit: "cover",
    videoRef: desktopVideoRef,
    controls: <_StyledVideoControls videoRef={desktopVideoRef} />
  }} externalVideoProps={{
    fit: "cover"
  }} /> : undefined} mediaMobile={media ? <Media {...takeFirst(media)} showCaption imageProps={{
    ratio: "3/2",
    sizes: "lg:50vw"
  }} inlineVideoProps={{
    ratio: "3/2",
    videoRef: mobileVideoRef,
    controls: <_StyledVideoControls2 videoRef={mobileVideoRef} />
  }} externalVideoProps={{
    ratio: "3/2"
  }} /> : undefined} mode={mode} shortDescription={shortDescription} text={text ? <RichText text={text} /> : undefined} title={title} value={id || ""} />;
}
var _StyledRadixAccordionHeader = /*#__PURE__*/_styled(RadixAccordion.Header).withConfig({
  componentId: "sc-sf-1jl2t0y-0"
})`${p => p.$_css}`;
var _StyledRadixAccordionTrigger = _styled2(RadixAccordion.Trigger).withConfig({
  componentId: "sc-sf-1jl2t0y-1"
})`${p => p.$_css2}`;
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-1jl2t0y-2"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "rowGap": "0.8rem",
  "@media (min-width: 1024px)": {
    "rowGap": "1.6rem"
  }
});
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-1jl2t0y-3"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "columnGap": "var(--spacing-lg)",
  "rowGap": "0.8rem",
  "@media (min-width: 1024px)": {
    "flexDirection": "row",
    "alignItems": "center"
  }
});
var _StyledSpan = _styled5("span").withConfig({
  componentId: "sc-sf-1jl2t0y-4"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "color": "var(--text-transparent)",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledH = _styled6("h2").withConfig({
  componentId: "sc-sf-1jl2t0y-5"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledP = _styled7("p").withConfig({
  componentId: "sc-sf-1jl2t0y-6"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledRadixAccordionContent = _styled8(RadixAccordion.Content).withConfig({
  componentId: "sc-sf-1jl2t0y-7"
})`${p => p.$_css3}`;
var _StyledGrid = _styled9(Grid).withConfig({
  componentId: "sc-sf-1jl2t0y-8"
})`${p => p.$_css4}`;
var _StyledDiv3 = _styled10("div").withConfig({
  componentId: "sc-sf-1jl2t0y-9"
})`${p => p.$_css5}`;
var _StyledDiv4 = _styled11("div").withConfig({
  componentId: "sc-sf-1jl2t0y-10"
})`${p => p.$_css6}`;
var _StyledDiv5 = _styled12("div").withConfig({
  componentId: "sc-sf-1jl2t0y-11"
})`${p => p.$_css7}`;
var _StyledDiv6 = _styled13("div").withConfig({
  componentId: "sc-sf-1jl2t0y-12"
})`${p => p.$_css8}`;
var _StyledDiv7 = _styled14("div").withConfig({
  componentId: "sc-sf-1jl2t0y-13"
})`${p => p.$_css9}`;
var _StyledDiv8 = _styled15("div").withConfig({
  componentId: "sc-sf-1jl2t0y-14"
})({
  "position": "relative",
  "gridColumn": "span 7 / span 7",
  "display": "none",
  "height": "100%",
  "padding": "var(--spacing-xl)",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv9 = _styled16("div").withConfig({
  componentId: "sc-sf-1jl2t0y-15"
})({
  "position": "relative",
  "height": "100%"
});
var _StyledDiv10 = _styled17("div").withConfig({
  componentId: "sc-sf-1jl2t0y-16"
})`${p => p.$_css10}`;
var _StyledDiv11 = _styled18("div").withConfig({
  componentId: "sc-sf-1jl2t0y-17"
})`${p => p.$_css11}`;
var _StyledDiv12 = _styled19("div").withConfig({
  componentId: "sc-sf-1jl2t0y-18"
})`${p => p.$_css12}`;
var _StyledVideoControls = _styled20(VideoControls).withConfig({
  componentId: "sc-sf-1jl2t0y-19"
})({
  "position": "absolute",
  "right": "var(--spacing-lg)",
  "top": "var(--spacing-lg)",
  "zIndex": "10"
});
var _StyledVideoControls2 = /*#__PURE__*/_styled21(VideoControls).withConfig({
  displayName: "accordion-list-item___StyledVideoControls2",
  componentId: "sc-dw2nys-0"
})({
  "position": "absolute",
  "right": "1.6rem",
  "top": "1.6rem",
  "zIndex": "10"
});